/**
 * Hook para manejar el texto hover y el cálculo de ancho máximo
 *
 * Este hook maneja la lógica de mostrar texto alternativo al hacer hover,
 * y calcula el ancho máximo necesario para evitar cambios de tamaño.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { ButtonColors } from '@/shared/types/types';

/**
 * Crea las variantes de color de texto usando CVA
 */
const textColorVariants = cva('', {
  variants: {
    color: {
      primary: '',
      secondary: '',
      tertiary: '',
      destructive: '',
      link: '',
      ghost: '',
      soft: '',
    },
    textAlign: {
      left: 'ui-text-left',
      center: 'ui-text-center',
      right: 'ui-text-right',
    },
  },
  defaultVariants: {
    color: 'primary',
    textAlign: 'left',
  },
});

interface UseHoverTextOptions {
  children: React.ReactNode;
  hoverText?: string;
  badgeFinishedLoading: boolean;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  textColor?: ButtonColors;
  textAlign?: 'left' | 'center' | 'right';
}

interface UseHoverTextReturn {
  isHovered: boolean;
  displayText: React.ReactNode;
  textStyle: React.CSSProperties | undefined;
  measureRef: React.RefObject<HTMLSpanElement>;
  textRef: React.RefObject<HTMLDivElement>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  measureSpanClassName: string;
}

/**
 * Hook que maneja la lógica de hover text y cálculo de ancho máximo
 *
 * @param options - Opciones del hook
 * @returns Estado y funciones relacionadas con hover text
 */
export const useHoverText = ({
  children,
  hoverText,
  badgeFinishedLoading,
  size,
  textColor,
  textAlign,
}: UseHoverTextOptions): UseHoverTextReturn => {
  const [isHovered, setIsHovered] = useState(false);
  const [maxTextWidth, setMaxTextWidth] = useState<number>(0);
  const measureRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  // Normalizar textos
  const childrenText = useMemo(() => {
    return children !== null && children !== false ? String(children).trim() : '';
  }, [children]);

  const hoverTextValue = useMemo(() => {
    return hoverText ? hoverText.trim() : '';
  }, [hoverText]);

  // Calcular el ancho máximo solo cuando:
  // 1. children cambia (childrenText)
  // 2. hoverText cambia (hoverTextValue)
  // 3. badgeFinishedLoading cambia a true (cuando el loading termina)
  useEffect(() => {
    // Solo calcular si hay hoverText y el badge ha terminado de cargar
    if (!hoverText || !badgeFinishedLoading || !measureRef.current) {
      return;
    }

    // Medir el ancho de ambos textos
    const measureWidth = (text: string): number => {
      if (!measureRef.current) return 0;
      measureRef.current.textContent = text;
      return measureRef.current.offsetWidth;
    };

    const childrenWidth = measureWidth(childrenText);
    const hoverTextWidth = measureWidth(hoverTextValue);

    // El ancho máximo es el mayor entre children y hoverText
    // Una vez que se establece, nunca se reduce
    const newMaxWidth = Math.max(childrenWidth, hoverTextWidth);

    // Solo actualizar si el nuevo ancho es mayor que el actual
    setMaxTextWidth((prevMaxWidth) => {
      if (newMaxWidth > prevMaxWidth) {
        return newMaxWidth;
      }
      return prevMaxWidth;
    });
  }, [childrenText, hoverTextValue, badgeFinishedLoading, hoverText]);

  // Resetear cuando badgeLoading se activa (vuelve a true)
  useEffect(() => {
    if (!badgeFinishedLoading) {
      setMaxTextWidth(0);
    }
  }, [badgeFinishedLoading]);

  // Determinar el texto a mostrar (memoizado para evitar re-renders)
  const displayText = useMemo(() => {
    if (!badgeFinishedLoading || !hoverText) {
      return children;
    }

    if (isHovered) {
      return hoverText;
    }

    return children;
  }, [badgeFinishedLoading, hoverText, isHovered, children]);

  // Handlers para hover (memoizados con useCallback)
  const handleMouseEnter = useCallback(() => {
    if (badgeFinishedLoading && hoverText) {
      setIsHovered(true);
    }
  }, [badgeFinishedLoading, hoverText]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Estilo inline memoizado para evitar recálculos
  const textStyle = useMemo(() => {
    if (badgeFinishedLoading && hoverText && maxTextWidth > 0) {
      return { minWidth: `${maxTextWidth}px` };
    }
    return undefined;
  }, [badgeFinishedLoading, hoverText, maxTextWidth]);

  // Clases para el span de medición
  const measureSpanClassName = useMemo(() => {
    return cn(
      'ui-absolute ui-opacity-0 ui-pointer-events-none ui-invisible ui-whitespace-nowrap ui-leading-none',
      textColorVariants({ color: textColor, textAlign: textAlign }),
      // Aplicar el mismo tamaño de fuente que el botón
      size === 'xxs' &&
        'ui-text-body-xs-bold ui-font-body-xs-bold ui-leading-body-xs-bold ui-tracking-body-xs-bold',
      size === 'xs' && 'ui-text-body-sm-bold',
      size === 'sm' && 'ui-text-body-sm-bold',
      size === 'md' && 'ui-text-body-sm-bold',
      size === 'lg' && 'ui-text-body-sm-bold',
    );
  }, [size, textColor, textAlign]);

  return {
    isHovered,
    displayText,
    textStyle,
    measureRef,
    textRef,
    handleMouseEnter,
    handleMouseLeave,
    measureSpanClassName,
  };
};
